// A declared vendor attribute, kept in the frontend by ruling 2026-10-16 until it moves to a vendordb column; never infer it from the numbers.

/** 'lp-list': wire lp is the list price and p the charged price. 'lp-charged': lp is the charged price and p the reference price. */
export type PriceFieldConvention = 'lp-list' | 'lp-charged';

/** What the scraper stores as a vendor's online reference: the list price it shows, or nothing usable. */
export type ReferenceKind = 'list' | 'none';

/** 'shown': the charged price on an out-of-stock day is the listing's price. 'unreliable': without a shown reduction it is not. */
export type OutOfStockPrice = 'shown' | 'unreliable';

/** 'level': the wire stock field is the stock level. 'zero-only': the vendor reports stock only when it is 0, so a missing reading means in stock. */
type StockReporting = 'level' | 'zero-only';

type VendorPriceConvention = {
  readonly fields: PriceFieldConvention;
  readonly reference: ReferenceKind;
  readonly outOfStockPrice: OutOfStockPrice;
  readonly stock: StockReporting;
};

export const DEFAULT_PRICE_FIELD_CONVENTION: PriceFieldConvention = 'lp-list';

export const DEFAULT_REFERENCE_KIND: ReferenceKind = 'list';

export const DEFAULT_OUT_OF_STOCK_PRICE: OutOfStockPrice = 'shown';

export const DEFAULT_STOCK_REPORTING: StockReporting = 'level';

// The reference kind is what each scraper was seen to store (spec decision 1, evidence of 2026-10-23), declared, never inferred.
const DECLARED: Readonly<Record<string, VendorPriceConvention>> = {
  apoteket: { fields: 'lp-list', reference: 'list', outOfStockPrice: 'shown', stock: 'level' },
  apohja: { fields: 'lp-list', reference: 'list', outOfStockPrice: 'shown', stock: 'level' },
  apotea: { fields: 'lp-charged', reference: 'list', outOfStockPrice: 'shown', stock: 'level' },
  // py_medp kronansapotek_se writes the in-store price here on days it differs from the online price; kronan-retail carries it daily.
  kronan: { fields: 'lp-charged', reference: 'none', outOfStockPrice: 'shown', stock: 'level' },
  doz: { fields: 'lp-charged', reference: 'list', outOfStockPrice: 'shown', stock: 'level' },
  // Prod Aug to Oct 2026: 192 of 398 stock-0 regular readings at Meds are followed by a discount again (spec decision 19);
  // ACO Sep 2026: a stock reading on 1263 of 5182 Meds days, 1247 of them 0 (spec decision 23).
  meds: {
    fields: 'lp-charged',
    reference: 'list',
    outOfStockPrice: 'unreliable',
    stock: 'zero-only',
  },
  apohem: { fields: 'lp-charged', reference: 'list', outOfStockPrice: 'shown', stock: 'level' },
};

/** The declared convention for a resolved vendor slug, or null when the vendor declares none. */
export function priceFieldConventionOf(vendor: string): PriceFieldConvention | null {
  return Object.hasOwn(DECLARED, vendor) ? DECLARED[vendor].fields : null;
}

/** The declared reference kind for a resolved vendor slug, or null when the vendor declares none. */
export function referenceKindOf(vendor: string): ReferenceKind | null {
  return Object.hasOwn(DECLARED, vendor) ? DECLARED[vendor].reference : null;
}

/** The declared stock reporting for a resolved vendor slug, or null when the vendor declares none. */
export function stockReportingOf(vendor: string): StockReporting | null {
  return Object.hasOwn(DECLARED, vendor) ? DECLARED[vendor].stock : null;
}

/** The declared out-of-stock price for a resolved vendor slug, or null when the vendor declares none. */
export function outOfStockPriceOf(vendor: string): OutOfStockPrice | null {
  return Object.hasOwn(DECLARED, vendor) ? DECLARED[vendor].outOfStockPrice : null;
}
